export const DEFAULT_TABLE_PAGE_SIZE = 10;
export const DEFAULT_TABLE_COL_WIDTH = 120;
export const TABLE_CHECKBOX_COL_WIDTH = 52;
export const TABLE_INDEX_COL_WIDTH = 48;

/** Fit header label + padding + optional sort icon (14px Inter semibold). */
export function estimateColumnMinWidth(header, options = {}) {
  const { sortable = false } = options;
  if (typeof header !== "string" || !header.trim()) {
    return DEFAULT_TABLE_COL_WIDTH;
  }
  const charWidth = 8;
  const basePadding = 40;
  const sortIconSpace = sortable ? 26 : 0;
  const estimated =
    Math.ceil(header.trim().length * charWidth) + basePadding + sortIconSpace;
  return Math.max(DEFAULT_TABLE_COL_WIDTH, estimated);
}

/** Sum of column widths so tables scroll horizontally instead of shrinking on small screens. */
export function computeTableMinWidth(columns, options = {}) {
  const {
    showCheckboxes = false,
    isIndexMode = false,
    explicitMinWidth,
  } = options;

  if (explicitMinWidth != null && explicitMinWidth !== "") {
    return typeof explicitMinWidth === "number"
      ? explicitMinWidth
      : Number.parseInt(String(explicitMinWidth), 10) || undefined;
  }

  if (!Array.isArray(columns) || columns.length === 0) return undefined;

  let sum = 0;
  if (showCheckboxes) sum += TABLE_CHECKBOX_COL_WIDTH;
  if (isIndexMode) sum += TABLE_INDEX_COL_WIDTH;

  for (const col of columns) {
    const { width, minWidth } = resolveColumnWidth(col);
    const isPercent =
      typeof width === "string" && String(width).trim().endsWith("%");
    sum += isPercent
      ? (minWidth ?? DEFAULT_TABLE_COL_WIDTH)
      : (typeof width === "number" ? width : minWidth ?? DEFAULT_TABLE_COL_WIDTH);
  }

  return sum > 0 ? sum : undefined;
}

export function resolveColumnWidth(col) {
  const headerText = col?.header ?? col?.label ?? "";
  const estimated = estimateColumnMinWidth(headerText, {
    sortable: Boolean(col?.sortable),
  });
  if (col?.width != null) {
    const isPercent =
      typeof col.width === "string" && String(col.width).trim().endsWith("%");
    const explicitMin =
      typeof col.minWidth === "number" ? col.minWidth : null;
    // Never allow minWidth below the header estimate — % widths under
    // table-layout:fixed still need a floor for horizontal scroll sizing.
    const minWidth = Math.max(estimated, explicitMin ?? 0);
    return {
      width: col.width,
      minWidth: isPercent ? minWidth : (explicitMin ?? col.width),
    };
  }
  if (col?.minWidth != null) {
    return {
      width: Math.max(estimated, col.minWidth),
      minWidth: Math.max(estimated, col.minWidth),
    };
  }
  return { width: estimated, minWidth: estimated };
}

/** Build page number list with ellipsis, e.g. [1, 2, "…", 6] */
export function buildPageList(current, total) {
  if (!total || total < 1) return [];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const want = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...want].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

/** Toggle sort direction when clicking the same column */
export function toggleSort(currentColumn, currentDirection, nextColumn) {
  if (currentColumn !== nextColumn) {
    return { column: nextColumn, direction: "asc" };
  }
  return {
    column: nextColumn,
    direction: currentDirection === "asc" ? "desc" : "asc",
  };
}

/**
 * Client-side row sort for DataTable pages.
 * @param {Array} rows
 * @param {string} columnId
 * @param {"asc"|"desc"} direction
 * @param {(row: unknown, columnId: string) => unknown} [getValue]
 */
export function clientSortRows(rows, columnId, direction, getValue) {
  if (!Array.isArray(rows) || rows.length === 0 || !columnId) return rows;
  const dir = direction === "asc" ? 1 : -1;
  const read =
    getValue ||
    ((row, col) => {
      const v = row?.[col];
      if (v == null) return "";
      if (typeof v === "object") return JSON.stringify(v);
      return v;
    });

  return [...rows].sort((a, b) => {
    const av = read(a, columnId);
    const bv = read(b, columnId);
    if (av == null && bv == null) return 0;
    if (av == null) return dir;
    if (bv == null) return -dir;
    if (typeof av === "number" && typeof bv === "number") {
      return dir * (av - bv);
    }
    const aDate =
      typeof av === "string" && !Number.isNaN(Date.parse(av))
        ? new Date(av).getTime()
        : null;
    const bDate =
      typeof bv === "string" && !Number.isNaN(Date.parse(bv))
        ? new Date(bv).getTime()
        : null;
    if (aDate != null && bDate != null) return dir * (aDate - bDate);
    return dir * String(av).localeCompare(String(bv), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
}

export function getRowId(row, rowKey, index) {
  if (typeof rowKey === "function") return String(rowKey(row, index));
  if (rowKey && row[rowKey] != null) return String(row[rowKey]);
  return String(index);
}
